import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import LandingNav from '@/pages/home/components/LandingNav';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { useSiteSettings } from '@/hooks/useSiteSettings';

interface LiveProduct {
  id: string;
  name: string;
  description: string;
  category: string;
  price_lifetime: number;
  requests_per_month: number;
  endpoints: number;
  icon: string;
  color: string;
  status: string;
}

const stats = [
  { value: '42+', label: 'APIs Available' },
  { value: '2.8M+', label: 'Transactions/day' },
  { value: '99.97%', label: 'Uptime SLA' },
  { value: '18,000+', label: 'Developers' },
];

const features = [
  { icon: 'ri-shield-keyhole-line', title: 'Encrypted API Keys', desc: 'HMAC-SHA256 hashed keys. Zero plaintext storage. Auto-revoke on breach detection.' },
  { icon: 'ri-rocket-line', title: 'Instant Delivery', desc: 'API access delivered automatically the moment your crypto payment confirms on-chain.' },
  { icon: 'ri-coins-line', title: 'Crypto Payments', desc: 'Pay with BTC, ETH, USDT, SOL or USDC via NOWPayments. No banks, no chargebacks.' },
  { icon: 'ri-infinity-line', title: 'Lifetime Licenses', desc: 'Buy once, use forever. 1-2yr, 3-5yr, and Lifetime plans available for every API.' },
  { icon: 'ri-terminal-box-line', title: 'API Tester Included', desc: 'Test every endpoint live from your developer dashboard before you write a single line of code.' },
  { icon: 'ri-bar-chart-2-line', title: 'Usage Analytics', desc: 'Real-time request volume, latency tracking, success rates, and error monitoring per API.' },
];

const steps = [
  { num: '01', title: 'Browse & Choose', desc: 'Explore 42+ production-grade APIs. Filter by category, pricing, or usage limits.', icon: 'ri-search-2-line' },
  { num: '02', title: 'Pay with Crypto', desc: 'Checkout with BTC, ETH, USDT, SOL or USDC. Invoice generated instantly.', icon: 'ri-secure-payment-line' },
  { num: '03', title: 'Get Instant Access', desc: 'API key delivered automatically on payment confirmation. Start building in minutes.', icon: 'ri-key-2-line' },
];

const plans = [
  { name: '1–2 Year', duration: '+2 years from purchase', badge: null, color: 'border-gray-200', priceLabel: 'From $19/yr', features: ['Full API access', 'Up to 2M req/month', 'Email support', 'Usage analytics'] },
  { name: 'Lifetime', duration: 'Never expires', badge: 'Most Popular', color: 'border-brand-500', priceLabel: 'From $99 once', features: ['Full API access', 'Unlimited requests', 'Priority support', 'Early access to new APIs', 'API Tester + Docs'] },
  { name: '3–5 Year', duration: '+5 years from purchase', badge: null, color: 'border-gray-200', priceLabel: 'From $15/yr', features: ['Full API access', 'Up to 5M req/month', 'Email support', 'Usage analytics'] },
];

const reviews = [
  // Column 1
  { name: 'Marcus Chen',    role: 'CTO @ DevHub',           img: 'https://readdy.ai/api/search-image?query=professional%20asian%20male%20developer%20headshot%20clean%20white%20background%20confident%20smiling%20modern%20portrait&width=72&height=72&seq=lp_r1&orientation=squarish',         content: 'PayStream API cut our payment integration from 3 weeks to 4 hours. The lifetime license paid for itself in the first week.',                                           stars: 5 },
  { name: 'Nina Laurent',   role: 'Head of Eng @ APIGrid',  img: 'https://readdy.ai/api/search-image?query=professional%20french%20female%20engineer%20headshot%20clean%20background%20confident%20smiling%20modern%20portrait%20photo&width=72&height=72&seq=lp_r4&orientation=squarish',     content: 'GeoLocate Plus powers location features for 3 million users. Rock-solid uptime, stupidly fast response times. Crypto checkout was seamless.',                        stars: 5 },
  { name: 'Alex Turner',    role: 'Backend Dev @ StackLab', img: 'https://readdy.ai/api/search-image?query=professional%20male%20software%20developer%20headshot%20clean%20background%20confident%20modern%20portrait%20photo&width=72&height=72&seq=lp_r5&orientation=squarish',             content: 'I was skeptical about paying with crypto but NOWPayments made it instant. Got my API key within seconds of the payment confirming on-chain.',                        stars: 5 },
  // Column 2
  { name: 'Priya Sharma',   role: 'Lead Engineer @ DataNest',img: 'https://readdy.ai/api/search-image?query=professional%20indian%20female%20engineer%20headshot%20clean%20background%20confident%20smiling%20modern%20portrait&width=72&height=72&seq=lp_r2&orientation=squarish',           content: 'AIContent Engine is the backbone of our entire marketing stack. The API docs are exceptional — zero guesswork.',                                                     stars: 5 },
  { name: 'David Kim',      role: 'Co-Founder @ APiLabs',   img: 'https://readdy.ai/api/search-image?query=professional%20korean%20male%20co-founder%20headshot%20clean%20background%20confident%20smiling%20modern%20portrait%20photo&width=72&height=72&seq=lp_r6&orientation=squarish',    content: 'The Developer Hub is hands-down the best DX I have seen in an API marketplace. Live tester, usage stats, key management — all in one place.',                      stars: 5 },
  { name: 'Emma Wilson',    role: 'CTO @ StackLab UK',      img: 'https://readdy.ai/api/search-image?query=professional%20british%20female%20cto%20headshot%20clean%20background%20confident%20smiling%20modern%20portrait%20photo&width=72&height=72&seq=lp_r7&orientation=squarish',        content: 'CryptoOracle feeds real-time price data into our entire trading system. 99.9% uptime with sub-100ms latency. Nothing else comes close at this price point.',        stars: 5 },
  // Column 3
  { name: 'Carlos Mendez',  role: 'Founder @ FusionAPI',    img: 'https://readdy.ai/api/search-image?query=professional%20latino%20male%20founder%20headshot%20clean%20background%20confident%20smiling%20modern%20portrait&width=72&height=72&seq=lp_r3&orientation=squarish',             content: 'Crypto payments + instant API delivery is a game-changer. No waiting, no friction. VerifyID Pro handles 50k KYC checks daily without breaking a sweat.',           stars: 5 },
  { name: 'Sofia Reyes',    role: 'Product Lead @ NexaCore', img: 'https://readdy.ai/api/search-image?query=professional%20latina%20female%20product%20manager%20headshot%20clean%20background%20confident%20smiling%20modern%20portrait%20photo&width=72&height=72&seq=lp_r8&orientation=squarish', content: 'Switched from a traditional API provider to API MART in a weekend. The migration was easy, the prices were better, and lifetime access is just unbeatable value.',  stars: 5 },
  { name: 'Jalen Brooks',   role: 'Senior Dev @ TechWave',  img: 'https://readdy.ai/api/search-image?query=professional%20black%20male%20senior%20developer%20headshot%20clean%20background%20confident%20smiling%20modern%20portrait%20photo&width=72&height=72&seq=lp_r9&orientation=squarish', content: 'VerifyID Pro handles onboarding for our entire user base. The HMAC key security gave our compliance team exactly what they needed to sign off.',                  stars: 5 },
];

// ── Scrolling testimonial column ────────────────────────────────────────
function TestimonialColumn({
  items,
  duration,
  className,
}: {
  items: typeof reviews;
  duration: number;
  className?: string;
}) {
  const doubled = [...items, ...items];
  return (
    <div className={cn('overflow-hidden', className)}>
      <motion.div
        animate={{ y: [0, `-${items.length * 100 / doubled.length}%`] }}
        transition={{ duration, repeat: Infinity, ease: 'linear', repeatType: 'loop' }}
        className="flex flex-col gap-4"
      >
        {doubled.map((r, i) => (
          <div key={`${r.name}-${i}`} className="bg-white/[0.06] border border-white/[0.09] rounded-2xl p-5 backdrop-blur-sm">
            <div className="flex items-center gap-1 mb-3">
              {Array.from({ length: r.stars }).map((_, j) => (
                <i key={j} className="ri-star-fill text-amber-400 text-xs" />
              ))}
            </div>
            <p className="text-sm text-white/75 leading-relaxed mb-4">&ldquo;{r.content}&rdquo;</p>
            <div className="flex items-center gap-3">
              <img src={r.img} alt={r.name} className="w-9 h-9 rounded-full object-cover shrink-0 ring-1 ring-white/10" />
              <div>
                <div className="text-xs font-semibold text-white/90">{r.name}</div>
                <div className="text-[10px] text-white/40">{r.role}</div>
              </div>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

// ── Footer newsletter form ───────────────────────────────────────────────
function FooterNewsletter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      const body = new URLSearchParams({ email: email.trim() }).toString();
      const res = await fetch('https://readdy.ai/api/form/d76trt5r5g02gnd6a6lg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (res.ok) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3.5">
        <i className="ri-checkbox-circle-fill text-green-400 text-lg shrink-0" />
        <div>
          <div className="text-sm font-semibold text-green-400">You&apos;re in!</div>
          <div className="text-xs text-white/40 mt-0.5">Check your inbox for a confirmation email.</div>
        </div>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="flex flex-col sm:flex-row gap-3"
    >
      <div className="flex-1 relative">
        <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
        <input
          type="email"
          name="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@company.com"
          required
          className="w-full bg-white/[0.06] border border-white/[0.10] text-white text-sm placeholder-white/25 rounded-xl pl-9 pr-4 py-3 focus:outline-none focus:border-brand-500/50 focus:bg-white/[0.08] transition-all"
        />
      </div>
      <button
        type="submit"
        disabled={status === 'loading'}
        className="px-5 py-3 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors shrink-0"
      >
        {status === 'loading' ? (
          <><i className="ri-loader-4-line animate-spin mr-2" />Subscribing…</>
        ) : (
          <><i className="ri-arrow-right-line mr-1.5" />Subscribe</>
        )}
      </button>
      {status === 'error' && (
        <p className="text-xs text-red-400 mt-1 w-full">Something went wrong. Please try again.</p>
      )}
    </form>
  );
}

// ── Elegant floating shape ────────────────────────────────────────────────
function ElegantShape({
  className,
  delay = 0,
  width = 400,
  height = 100,
  rotate = 0,
  gradient = 'from-white/[0.06]',
}: {
  className?: string;
  delay?: number;
  width?: number;
  height?: number;
  rotate?: number;
  gradient?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -120, rotate: rotate - 15 }}
      animate={{ opacity: 1, y: 0, rotate }}
      transition={{ duration: 2.4, delay, ease: [0.23, 0.86, 0.39, 0.96], opacity: { duration: 1.2 } }}
      className={cn('absolute', className)}
    >
      <motion.div
        animate={{ y: [0, 18, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        style={{ width, height }}
        className="relative"
      >
        <div
          className={cn(
            'absolute inset-0 rounded-full',
            'bg-gradient-to-r to-transparent',
            gradient,
            'backdrop-blur-[2px] border border-white/[0.12]',
            'shadow-[0_8px_32px_0_rgba(255,255,255,0.06)]',
            'after:absolute after:inset-0 after:rounded-full',
            'after:bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.12),transparent_70%)]'
          )}
        />
      </motion.div>
    </motion.div>
  );
}

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

export default function HomePage() {
  const navigate = useNavigate();
  const apisRef = useRef<HTMLDivElement>(null);
  const [liveProducts, setLiveProducts] = useState<LiveProduct[]>([]);
  const { settings: siteSettings } = useSiteSettings();
  const siteLogo = siteSettings.site_logo_url ?? '';
  const siteName = siteSettings.site_name || 'API MART';

  useEffect(() => {
    supabase
      .from('products')
      .select('id, name, description, category, price_lifetime, requests_per_month, endpoints, icon, color, status')
      .eq('status', 'active')
      .order('created_at', { ascending: true })
      .limit(6)
      .then(({ data }) => {
        if (data && data.length > 0) setLiveProducts(data as LiveProduct[]);
      });
  }, []);

  const fadeUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.9, delay: 0.4 + i * 0.18, ease: [0.25, 0.4, 0.25, 1] },
    }),
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <LandingNav />

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0c0e12]">

        {/* Soft ambient gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-500/[0.06] via-transparent to-amber-500/[0.04] blur-3xl pointer-events-none" />

        {/* Floating shapes layer */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <ElegantShape
            delay={0.3} width={640} height={150} rotate={12}
            gradient="from-brand-500/[0.18]"
            className="left-[-12%] top-[18%]"
          />
          <ElegantShape
            delay={0.5} width={520} height={130} rotate={-14}
            gradient="from-amber-500/[0.14]"
            className="right-[-8%] top-[68%]"
          />
          <ElegantShape
            delay={0.4} width={320} height={88} rotate={-7}
            gradient="from-emerald-500/[0.13]"
            className="left-[6%] bottom-[8%]"
          />
          <ElegantShape
            delay={0.6} width={220} height={64} rotate={22}
            gradient="from-yellow-500/[0.13]"
            className="right-[18%] top-[10%]"
          />
          <ElegantShape
            delay={0.7} width={160} height={48} rotate={-28}
            gradient="from-orange-400/[0.12]"
            className="left-[28%] top-[7%]"
          />
          <ElegantShape
            delay={0.55} width={260} height={72} rotate={16}
            gradient="from-brand-400/[0.10]"
            className="right-[5%] top-[35%]"
          />
        </div>

        {/* Fade vignette top + bottom */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0c0e12]/60 via-transparent to-[#0c0e12]/80 pointer-events-none" />

        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto w-full pt-20">

          {/* Badge */}
          <motion.div
            custom={0}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
            className="inline-flex items-center gap-2.5 bg-white/[0.06] backdrop-blur-sm border border-white/[0.10] px-4 py-2 rounded-full mb-8"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse shrink-0" />
            <span className="text-[11px] text-white/70 font-mono tracking-widest uppercase">42 APIs Live · 99.97% Uptime</span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            custom={1}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
            className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[1.08] tracking-tight mb-6"
          >
            ONE API.
            <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-400 via-amber-400 to-brand-300">
              INFINITE
            </span>{' '}
            TRANSACTIONS.
            <br />
            <span className="text-white/90">ZERO FRICTION.</span>
          </motion.h1>

          {/* Subheading */}
          <motion.p
            custom={2}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
            className="text-base md:text-lg text-white/50 max-w-2xl mx-auto leading-relaxed font-light tracking-wide"
          >
            The professional API marketplace for developers. Pay with crypto, get instant access.
            Payments, identity, AI — production-grade APIs with lifetime licenses.
          </motion.p>

          {/* CTA buttons */}
          <motion.div
            custom={3}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button
              onClick={() => navigate('/marketplace')}
              className="px-8 py-4 bg-brand-500 hover:bg-brand-600 text-white font-bold text-sm rounded-xl cursor-pointer whitespace-nowrap transition-all"
            >
              <i className="ri-store-line mr-2" />Browse APIs
            </button>
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-4 bg-white/[0.07] hover:bg-white/[0.12] backdrop-blur-sm border border-white/[0.15] text-white font-semibold text-sm rounded-xl cursor-pointer whitespace-nowrap transition-all"
            >
              <i className="ri-user-add-line mr-2" />Create Free Account
            </button>
          </motion.div>

          {/* Trust row */}
          <motion.div
            custom={4}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
            className="mt-12 flex flex-wrap items-center justify-center gap-6 text-[11px] text-white/30 font-mono tracking-wide"
          >
            {['BTC', 'ETH', 'USDT', 'SOL', 'USDC'].map(c => (
              <span key={c} className="flex items-center gap-1.5">
                <i className="ri-checkbox-circle-fill text-brand-500/70" />{c}
              </span>
            ))}
            <span className="flex items-center gap-1.5">
              <i className="ri-shield-check-fill text-green-500/70" />HMAC-SHA512
            </span>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-8 left-0 right-0 flex justify-center z-10">
          <motion.button
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2.2 }}
            onClick={() => document.getElementById('stats')?.scrollIntoView({ behavior: 'smooth' })}
            className="text-white/30 hover:text-white/60 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-down-line text-2xl" />
          </motion.button>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section id="stats" className="bg-sidebar border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <motion.div key={s.label} {...fadeUp(i * 0.07)} className="text-center">
              <div className="text-2xl md:text-3xl font-extrabold text-brand-400">{s.value}</div>
              <div className="text-xs text-white/50 mt-1">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <span className="text-xs font-bold text-brand-500 tracking-widest uppercase">Why API MART</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">Built for developers who ship fast</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto text-sm leading-relaxed">Enterprise-grade infrastructure, developer-first experience, crypto-native payments.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div key={f.title} {...fadeUp(i * 0.07)} className="p-6 rounded-2xl border border-gray-100 hover:border-brand-100 hover:bg-brand-50/20 transition-all group">
                <div className="w-11 h-11 bg-brand-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-brand-100 transition-colors">
                  <i className={`${f.icon} text-xl text-brand-500`} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURED APIS ── */}
      <section id="apis" className="py-24 px-6 bg-gray-50/60">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <span className="text-xs font-bold text-brand-500 tracking-widest uppercase">API Catalog</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">Production-ready APIs, instant access</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto text-sm">From payments to AI — every API ships with docs, a live tester, and usage analytics.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {liveProducts.map((p, i) => (
              <motion.div key={p.id} {...fadeUp(i * 0.06)} className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-brand-100 transition-all group cursor-pointer" onClick={() => navigate('/marketplace')}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${p.color}18` }}>
                    <i className={`${p.icon} text-xl`} style={{ color: p.color }} />
                  </div>
                  <span className="text-[11px] bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full font-medium">{p.category}</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{p.name}</h3>
                <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-2">{p.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-400 border-t border-gray-100 pt-3">
                  <span><i className="ri-terminal-line mr-1" />{p.endpoints} endpoints</span>
                  <span><i className="ri-bar-chart-line mr-1" />{(p.requests_per_month / 1000000).toFixed(1)}M req/mo</span>
                  <span className="font-bold text-gray-900">From ${p.price_lifetime}</span>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp(0.3)} className="text-center mt-10">
            <button onClick={() => navigate('/marketplace')} className="px-8 py-3.5 border-2 border-sidebar text-sidebar font-semibold rounded-xl hover:bg-sidebar hover:text-white transition-all cursor-pointer whitespace-nowrap text-sm">
              View All APIs <i className="ri-arrow-right-line ml-1" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="howitworks" className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <span className="text-xs font-bold text-brand-500 tracking-widest uppercase">Simple Process</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">From zero to production in 3 steps</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={s.num} {...fadeUp(i * 0.12)} className="text-center">
                <div className="relative inline-flex items-center justify-center mb-5">
                  <div className="w-16 h-16 bg-sidebar rounded-2xl flex items-center justify-center">
                    <i className={`${s.icon} text-2xl text-white`} />
                  </div>
                  <span className="absolute -top-2 -right-2 w-6 h-6 bg-brand-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{s.num.slice(-1)}</span>
                </div>
                <div className="text-[11px] font-mono text-brand-500 tracking-widest mb-2">{s.num}</div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-24 px-6 bg-gray-50/60">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <span className="text-xs font-bold text-brand-500 tracking-widest uppercase">Pricing Plans</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">Buy once. Build forever.</h2>
            <p className="text-gray-500 mt-3 text-sm">All plans include full API access. Pay with crypto — no subscriptions, no recurring billing.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan, i) => (
              <motion.div key={plan.name} {...fadeUp(i * 0.1)} className={`relative bg-white rounded-2xl border-2 p-7 ${plan.color} ${plan.badge ? 'ring-2 ring-brand-500/20' : ''}`}>
                {plan.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-500 text-white text-[11px] font-bold px-3 py-1 rounded-full whitespace-nowrap">{plan.badge}</span>
                )}
                <div className="mb-5">
                  <h3 className="font-bold text-gray-900 text-lg">{plan.name}</h3>
                  <p className="text-xs text-gray-400 mt-1">{plan.duration}</p>
                  <div className="mt-4 text-2xl font-extrabold text-gray-900">{plan.priceLabel}</div>
                  <div className="text-[11px] text-gray-400 mt-0.5">per API · paid in crypto</div>
                </div>
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-gray-600">
                      <i className="ri-check-line text-brand-500 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate('/marketplace')} className={`w-full py-3 rounded-xl font-semibold text-sm cursor-pointer whitespace-nowrap transition-all ${plan.badge ? 'bg-brand-500 hover:bg-brand-600 text-white' : 'border-2 border-sidebar text-sidebar hover:bg-sidebar hover:text-white'}`}>
                  Browse APIs <i className="ri-arrow-right-line ml-1" />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REVIEWS ── */}
      <section id="reviews" className="py-24 px-6 bg-[#0c0e12] overflow-hidden">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <motion.div {...fadeUp()} className="text-center mb-14">
            <span className="text-xs font-bold text-brand-500 tracking-widest uppercase">Testimonials</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3">Trusted by 18,000+ developers worldwide</h2>
            <p className="text-white/40 mt-3 text-sm max-w-md mx-auto">Real developers. Real results. Shipped with API MART.</p>
          </motion.div>

          {/* Scrolling columns */}
          <div
            className="flex gap-4 justify-center"
            style={{ maskImage: 'linear-gradient(to bottom, transparent, black 14%, black 86%, transparent)', WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 14%, black 86%, transparent)' }}
          >
            {/* Column 1 — always visible */}
            <TestimonialColumn
              items={reviews.slice(0, 3)}
              duration={20}
              className="w-full max-w-xs"
            />
            {/* Column 2 — hidden on small screens */}
            <TestimonialColumn
              items={reviews.slice(3, 6)}
              duration={26}
              className="hidden md:block w-full max-w-xs"
            />
            {/* Column 3 — hidden on medium screens */}
            <TestimonialColumn
              items={reviews.slice(6, 9)}
              duration={22}
              className="hidden lg:block w-full max-w-xs"
            />
          </div>

          {/* CTA below */}
          <motion.div {...fadeUp(0.2)} className="text-center mt-12">
            <button
              onClick={() => navigate('/register')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/[0.07] hover:bg-white/[0.12] border border-white/[0.12] text-white/80 hover:text-white text-sm font-medium rounded-xl cursor-pointer whitespace-nowrap transition-all"
            >
              <i className="ri-user-add-line" />Join 18,000+ developers
            </button>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 px-6 bg-sidebar">
        <div className="relative max-w-4xl mx-auto text-center overflow-hidden">
          <motion.div {...fadeUp()}>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-5 leading-tight">
              Start building with<br /><span className="text-brand-400">production APIs</span> today
            </h2>
            <p className="text-white/60 text-base mb-10 max-w-xl mx-auto">Join 18,000+ developers. Buy with crypto, get instant access, ship faster.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => navigate('/marketplace')} className="px-8 py-4 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl cursor-pointer whitespace-nowrap transition-all text-sm">
                <i className="ri-store-line mr-2" />Browse APIs
              </button>
              <button onClick={() => navigate('/register')} className="px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-all text-sm">
                <i className="ri-user-add-line mr-2" />Create Free Account
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#0c0e12] text-white/50 border-t border-white/[0.06]">

        {/* Top — brand + newsletter */}
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start border-b border-white/[0.06]">

          {/* Brand block */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              {siteLogo ? (
                <img src={siteLogo} alt={siteName} className="h-10 max-w-[180px] object-contain" />
              ) : (
                <>
                  <div className="w-9 h-9 bg-brand-500 rounded-xl flex items-center justify-center shrink-0">
                    <i className="ri-code-box-line text-white text-base" />
                  </div>
                  <div>
                    <div className="font-bold text-white text-lg tracking-wide">{siteName}</div>
                    <div className="text-[10px] text-white/30 font-mono tracking-widest">ONE API. INFINITE TXN.</div>
                  </div>
                </>
              )}
            </div>
            <p className="text-sm text-white/40 leading-relaxed max-w-sm">
              The API marketplace built for serious developers. Buy once, ship forever.
              Crypto-native payments, instant key delivery, lifetime licenses.
            </p>
            <div className="flex items-center gap-3 mt-6">
              {[
                { icon: 'ri-github-fill', label: 'GitHub' },
                { icon: 'ri-twitter-x-fill', label: 'X (Twitter)' },
                { icon: 'ri-discord-fill', label: 'Discord' },
                { icon: 'ri-linkedin-box-fill', label: 'LinkedIn' },
              ].map(s => (
                <a key={s.label} href="#" aria-label={s.label} rel="nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/[0.05] hover:bg-brand-500/20 hover:text-brand-400 text-white/40 border border-white/[0.07] transition-all cursor-pointer">
                  <i className={`${s.icon} text-base`} />
                </a>
              ))}
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <div className="inline-flex items-center gap-2 bg-brand-500/10 border border-brand-500/20 px-3 py-1.5 rounded-full mb-4">
              <i className="ri-mail-send-line text-brand-400 text-xs" />
              <span className="text-xs text-brand-400 font-medium">Developer Newsletter</span>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Stay ahead of the API curve</h3>
            <p className="text-sm text-white/40 mb-5 leading-relaxed">
              New APIs, platform updates, crypto payment tips and developer deep-dives. No spam, unsubscribe anytime.
            </p>
            <FooterNewsletter />
          </div>
        </div>

        {/* Middle — nav columns */}
        <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 border-b border-white/[0.06]">
          {[
            {
              title: 'Product',
              links: [
                { label: 'Marketplace', path: '/marketplace' },
                { label: 'Pricing', id: 'pricing' },
                { label: 'Features', id: 'features' },
                { label: 'How It Works', id: 'howitworks' },
              ],
            },
            {
              title: 'Developers',
              links: [
                { label: 'API Docs', path: '/developer' },
                { label: 'API Tester', path: '/developer' },
                { label: 'Usage Stats', path: '/developer' },
                { label: 'Webhook Docs', path: '/payments' },
              ],
            },
            {
              title: 'Account',
              links: [
                { label: 'Sign In', path: '/login' },
                { label: 'Register', path: '/register' },
                { label: 'Dashboard', path: '/dashboard' },
                { label: 'My Subscriptions', path: '/subscriptions' },
              ],
            },
            {
              title: 'Legal',
              links: [
                { label: 'Privacy Policy', path: '/privacy-policy' },
                { label: 'Terms & Conditions', path: '/terms' },
                { label: 'Support Center', path: '/support' },
                { label: 'Contact Us', path: '/support' },
              ],
            },
          ].map(col => (
            <div key={col.title}>
              <div className="text-white font-semibold text-xs uppercase tracking-widest mb-4">{col.title}</div>
              <ul className="space-y-2.5">
                {col.links.map(link => (
                  <li key={link.label}>
                    <button
                      onClick={() => {
                        if ('path' in link && link.path) navigate(link.path);
                        else if ('id' in link && link.id) document.getElementById(link.id)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="text-xs text-white/35 hover:text-white/80 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <span className="text-xs text-white/25">&copy; {new Date().getFullYear()} API MART. All rights reserved.</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/privacy-policy')}
                className="text-xs text-white/35 hover:text-white/70 cursor-pointer whitespace-nowrap transition-colors"
              >
                Privacy Policy
              </button>
              <span className="text-white/15">·</span>
              <button
                onClick={() => navigate('/terms')}
                className="text-xs text-white/35 hover:text-white/70 cursor-pointer whitespace-nowrap transition-colors"
              >
                Terms &amp; Conditions
              </button>
              <span className="text-white/15">·</span>
              <button
                onClick={() => navigate('/support')}
                className="text-xs text-white/35 hover:text-white/70 cursor-pointer whitespace-nowrap transition-colors"
              >
                Support
              </button>
            </div>
          </div>

          {/* Accepted crypto */}
          <div className="flex items-center gap-2">
            {[
              { label: 'BTC', color: '#f7931a' },
              { label: 'ETH', color: '#8c9eff' },
              { label: 'USDT', color: '#26a17b' },
              { label: 'SOL', color: '#9945ff' },
              { label: 'USDC', color: '#2775ca' },
            ].map(c => (
              <span key={c.label} className="text-[10px] font-mono font-bold px-2 py-0.5 rounded border"
                style={{ color: c.color, borderColor: `${c.color}30`, backgroundColor: `${c.color}10` }}>
                {c.label}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-1.5 text-xs text-white/25">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
            All systems operational
          </div>
        </div>
      </footer>
    </div>
  );
}
